(() => {
  if (window.FoxThemeProductCardVariantRailInitialized) return;
  window.FoxThemeProductCardVariantRailInitialized = true;

  const selectors = {
    variantRailLink: '[data-product-card-variant-link]',
    variantRail: '[data-product-card-variant-rail]',
    variantRailTrack: '.product-card__variant-rail-track',
    mainImage: '.product-card__image--main img',
  };
  const dragThreshold = 4;
  const draggedRails = new WeakSet();
  const preloadedImages = new Map();
  const preloadQueue = [];
  const observedRails = new Set();
  const nearbyRails = new WeakSet();
  const maxConcurrentPreloads = 3;
  const maxCachedPreloads = 48;
  const maxQueuedPreloads = 48;
  const automaticVariantLimit = 6;
  let activePreloads = 0;
  let preloadObserver;
  const dragState = {
    rail: null,
    track: null,
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    isDragging: false,
  };

  function canPreloadVariants() {
    const connection = navigator.connection;
    return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
  }

  function trimPreloadedImages() {
    let loadedCount = [...preloadedImages.values()].filter((entry) => entry.state === 'loaded').length;
    for (const [key, entry] of preloadedImages) {
      if (loadedCount <= maxCachedPreloads) break;
      if (entry.state === 'loaded') {
        preloadedImages.delete(key);
        loadedCount -= 1;
      }
    }
    while (preloadQueue.length > maxQueuedPreloads) {
      preloadedImages.delete(preloadQueue.pop().key);
    }
  }

  function isPreloadNearby(entry) {
    if (!entry.rail?.isConnected) return false;
    if (!entry.priority) return nearbyRails.has(entry.rail);
    // Intent changes queue order, but must not keep downloading a card scrolled away.
    const rect = entry.rail.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom >= -200 && rect.top <= window.innerHeight + 200;
  }

  function drainPreloadQueue() {
    while (activePreloads < maxConcurrentPreloads && preloadQueue.length) {
      const entry = preloadQueue.shift();
      if (!canPreloadVariants() || !isPreloadNearby(entry)) {
        preloadedImages.delete(entry.key);
        continue;
      }

      entry.state = 'loading';
      activePreloads += 1;
      const image = new Image();
      entry.image = image;
      image.decoding = 'async';
      image.fetchPriority = 'low';
      const finish = (loaded) => {
        if (entry.state !== 'loading') return;
        window.clearTimeout(timeout);
        image.onload = image.onerror = null;
        entry.state = 'loaded';
        // Completed images need no references to cards removed by filters or pagination.
        entry.rail = null;
        activePreloads -= 1;
        if (!loaded) {
          image.removeAttribute('srcset');
          image.removeAttribute('src');
          preloadedImages.delete(entry.key);
        }
        trimPreloadedImages();
        drainPreloadQueue();
      };
      const timeout = window.setTimeout(() => finish(false), 15000);
      image.onload = () => {
        image.onload = image.onerror = null;
        if (typeof image.decode === 'function') {
          image.decode().catch(() => {}).then(() => finish(true));
        } else {
          finish(true);
        }
      };
      image.onerror = () => finish(false);
      // Match the displayed image's responsive candidate; preloading the 900px fallback
      // alone would still leave the browser downloading a different srcset URL on hover.
      if (entry.sizes) image.sizes = entry.sizes;
      if (entry.srcset) image.srcset = entry.srcset;
      image.src = entry.src;
    }
  }

  function variantImageSrcset(link) {
    const legacy = link.dataset.productCardImageSrcset;
    if (legacy) return legacy;
    const src = link.dataset.productCardImageSrc || '';
    const widths = link.dataset.productCardImageWidths || '';
    if (!/^[1-9]\d*(?:,[1-9]\d*)*$/.test(widths)) return '';
    const candidates = widths.split(',');
    if (candidates.some(width => Number(width) > 5760)) return '';
    try {
      const url = new URL(src, window.location.href);
      const isShopifyImage = (url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/')) ||
        (url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/'));
      if (!/^https?:$/.test(url.protocol) || !isShopifyImage || url.hash || url.searchParams.getAll('width').length !== 1) return '';
      const parameter = [...src.matchAll(/([?&])width=\d+(?=&|$)/g)];
      if (parameter.length !== 1) return '';
      // Preserve authored URL encoding, other query parameters and descriptor order.
      // Liquid emits this compact form only after verifying each original URL matches.
      const match = parameter[0];
      return candidates.map(width => `${src.slice(0, match.index)}${match[1]}width=${width}${src.slice(match.index + match[0].length)} ${width}w`).join(', ');
    } catch (_) {
      return '';
    }
  }

  function queueVariantPreload(link, priority = false) {
    if (!canPreloadVariants()) return;
    const src = link.dataset.productCardImageSrc;
    if (!src) return;
    const srcset = variantImageSrcset(link);
    const sizes = link.dataset.productCardImageSizes || link.closest('.product-card')?.querySelector(selectors.mainImage)?.sizes || '';
    const key = JSON.stringify([src, srcset, sizes, window.innerWidth, window.devicePixelRatio]);
    const existing = preloadedImages.get(key);
    if (existing) {
      if (priority && existing.state === 'queued') {
        existing.priority = true;
        existing.rail = link.closest(selectors.variantRail);
        preloadQueue.splice(preloadQueue.indexOf(existing), 1);
        preloadQueue.unshift(existing);
      }
      // Keep recently used decoded images in the bounded cache.
      preloadedImages.delete(key);
      preloadedImages.set(key, existing);
      return;
    }
    const entry = { key, src, srcset, sizes, priority, rail: link.closest(selectors.variantRail), state: 'queued' };
    preloadedImages.set(key, entry);
    if (priority) preloadQueue.unshift(entry);
    else preloadQueue.push(entry);
    trimPreloadedImages();
  }

  function preloadRail(rail, priority = false, selectedLink = null) {
    if (!canPreloadVariants()) return;
    const links = [...rail.querySelectorAll(selectors.variantRailLink)];
    const candidates = priority ? links.reverse() : links.slice(0, automaticVariantLimit);
    candidates.forEach((link) => queueVariantPreload(link, priority));
    if (selectedLink) queueVariantPreload(selectedLink, true);
    drainPreloadQueue();
  }

  function handleCardPreloadIntent(event) {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('.product-card');
    if (!card) return;
    const previous = event.relatedTarget instanceof Element ? event.relatedTarget.closest('.product-card') : null;
    if (event.type === 'pointerover' && previous === card) return;
    const selectedLink = event.target.closest(selectors.variantRailLink);
    card.querySelectorAll(selectors.variantRail).forEach((rail) => preloadRail(rail, true, selectedLink));
  }

  function observeVariantRails(root) {
    const rails = [...root.querySelectorAll(selectors.variantRail)];
    if (root.matches?.(selectors.variantRail)) rails.unshift(root);
    rails.forEach((rail) => {
      if (observedRails.has(rail)) return;
      observedRails.add(rail);
      preloadObserver.observe(rail);
    });
  }

  function startVariantPreloading() {
    if (!('IntersectionObserver' in window)) return;
    preloadObserver = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          nearbyRails.add(target);
          preloadRail(target);
        } else {
          nearbyRails.delete(target);
        }
      });
    }, { rootMargin: '200px' });
    observeVariantRails(document);
    // Collection filters, infinite scrolling and the theme editor replace cards in place.
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) observeVariantRails(node);
        });
      });
      observedRails.forEach((rail) => {
        if (rail.isConnected) return;
        preloadObserver.unobserve(rail);
        observedRails.delete(rail);
        nearbyRails.delete(rail);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function activateVariantRailLink(link) {
    queueVariantPreload(link, true);
    drainPreloadQueue();
    const rail = link.closest(selectors.variantRail);
    const card = link.closest('.product-card');
    const mainImage = card?.querySelector(selectors.mainImage);
    const imageSrc = link.dataset.productCardImageSrc;
    const imageSrcset = variantImageSrcset(link);
    const imageSizes = link.dataset.productCardImageSizes;
    const imageAlt = link.dataset.productCardImageAlt;

    rail?.querySelectorAll(`${selectors.variantRailLink}[aria-current="true"]`).forEach((item) => {
      item.removeAttribute('aria-current');
      item.classList.remove('is-active');
    });

    link.setAttribute('aria-current', 'true');
    link.classList.add('is-active');

    if (!mainImage || !imageSrc) return;

    if (imageSizes) {
      mainImage.setAttribute('sizes', imageSizes);
    }

    if (imageSrcset) {
      mainImage.setAttribute('srcset', imageSrcset);
    } else {
      mainImage.removeAttribute('srcset');
    }

    if (mainImage.getAttribute('src') !== imageSrc) {
      mainImage.setAttribute('src', imageSrc);
    }

    if (imageAlt) {
      mainImage.setAttribute('alt', imageAlt);
    }
  }

  function handleVariantRailEvent(event) {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest(selectors.variantRailLink);
    if (!link) return;

    const rail = link.closest(selectors.variantRail);

    if (event.type === 'mouseover') {
      if (event.buttons !== 0 || (rail && draggedRails.has(rail))) return;

      const previousTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
      const previousLink = previousTarget?.closest(selectors.variantRailLink);
      if (previousLink === link) return;
    }

    if (event.type === 'click') {
      const isPlainPrimaryClick =
        event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.detail !== 0;

      if (!isPlainPrimaryClick) return;
      event.preventDefault();
    }

    activateVariantRailLink(link);
  }

  function resetVariantRailDragState() {
    dragState.rail?.classList.remove('is-dragging');
    dragState.track?.classList.remove('is-dragging');
    dragState.rail = null;
    dragState.track = null;
    dragState.pointerId = null;
    dragState.startX = 0;
    dragState.startScrollLeft = 0;
    dragState.isDragging = false;
  }

  function handleVariantRailPointerDown(event) {
    if (!(event.target instanceof Element)) return;
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const track = event.target.closest(selectors.variantRailTrack);
    if (!track || track.scrollWidth <= track.clientWidth) return;

    dragState.rail = track.closest(selectors.variantRail);
    dragState.track = track;
    dragState.pointerId = event.pointerId;
    dragState.startX = event.clientX;
    dragState.startScrollLeft = track.scrollLeft;
    dragState.isDragging = false;

    track.setPointerCapture?.(event.pointerId);
  }

  function handleVariantRailPointerMove(event) {
    if (!dragState.track || event.pointerId !== dragState.pointerId) return;

    const deltaX = event.clientX - dragState.startX;
    if (!dragState.isDragging && Math.abs(deltaX) < dragThreshold) return;

    if (!dragState.isDragging) {
      dragState.isDragging = true;
      dragState.rail?.classList.add('is-dragging');
      dragState.track.classList.add('is-dragging');
    }

    dragState.track.scrollLeft = dragState.startScrollLeft - deltaX;
    event.preventDefault();
  }

  function handleVariantRailPointerEnd(event) {
    if (!dragState.track || event.pointerId !== dragState.pointerId) return;

    const rail = dragState.rail;
    const track = dragState.track;
    const shouldSuppressClick = dragState.isDragging && rail;

    if (track.hasPointerCapture?.(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }

    resetVariantRailDragState();

    if (!shouldSuppressClick) return;

    draggedRails.add(rail);
    window.setTimeout(() => {
      draggedRails.delete(rail);
    }, 250);
  }

  function suppressVariantRailClickAfterDrag(event) {
    if (!(event.target instanceof Element)) return;

    const rail = event.target.closest(selectors.variantRail);
    if (!rail || !draggedRails.has(rail)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function preventVariantRailNativeDrag(event) {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(selectors.variantRail)) return;

    event.preventDefault();
  }

  document.addEventListener('click', suppressVariantRailClickAfterDrag, true);
  document.addEventListener('pointerover', handleCardPreloadIntent, { passive: true });
  document.addEventListener('pointerdown', handleCardPreloadIntent, { passive: true });
  document.addEventListener('focusin', handleCardPreloadIntent);
  document.addEventListener('dragstart', preventVariantRailNativeDrag, true);
  document.addEventListener('click', handleVariantRailEvent);
  document.addEventListener('focusin', handleVariantRailEvent);
  document.addEventListener('mouseover', handleVariantRailEvent);
  document.addEventListener('pointerdown', handleVariantRailPointerDown);
  document.addEventListener('pointermove', handleVariantRailPointerMove, { passive: false });
  document.addEventListener('pointerup', handleVariantRailPointerEnd);
  document.addEventListener('pointercancel', handleVariantRailPointerEnd);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startVariantPreloading, { once: true });
  } else {
    startVariantPreloading();
  }
})();
